import {
	InvoiceWithPaymentsViewType,
} from "@/lib/supabase/supabaseTypes";
import { Row } from "@tanstack/react-table";
import { ReminderInvoiceRef } from "../mahnwesen/payment-reminder.types";

// Payment detail interface (since payments field comes as Json from Supabase)
export interface PaymentDetail {
	allocation_id: number;
	allocated_amount: number;
	allocation_note?: string | null;
	allocation_created_at: string;
	allocation_canceled_at?: string | null;
	allocation_type: number;
	payment_id: number | null;
	payment_value: number | null;
	payment_currency: string | null;
	payment_valuta_date: string | null;
	payment_entry_date: string | null;
	payment_created_at: string | null;
	payment_canceled_at?: string | null;
	payment_note_1?: string;
	payment_note_2?: string;
	payment_type: string;
	payment_esr_code?: string;
	payment_esr_data?: string;
	payment_bank_account_id?: number;
	is_loss: boolean;
}

// Base row data interface for table rows
export interface BaseTableRowData {
	isPaymentRow?: boolean;
}

// Simple table row types for invoice and payment rows
export interface TableInvoiceRow extends InvoiceWithPaymentsView {
	isPaymentRow: false;
	reminder_stage?: number | null;
	reminder_paused?: boolean | null;
	reminder_paused_until?: string | null;
	reminder_stop_reason?: string | null;
	reminder_share_state?: string | null;
	subRows?: TablePaymentRow[];
}

export interface TablePaymentRow extends PaymentDetail {
	invoice_id: number;
	isPaymentRow: true;
}

// Union type for all table row data - much simpler!
export type TableRowData = TableInvoiceRow | TablePaymentRow;

// Context menu data interface with discriminated union
export type ContextMenuData<T extends BaseTableRowData = TableRowData> =
	| {
		visible: false;
		x: number;
		y: number;
		row: null;
	}
	| {
		visible: true;
		x: number;
		y: number;
		row: T;
	};

export interface InvoiceReminderActions {
	onCreateReminder: (invoice: ReminderInvoiceRef) => void;
	onPauseReminders: (invoice: ReminderInvoiceRef) => void;
	onResumeReminders: (invoice: ReminderInvoiceRef) => void;
	onShowReminderHistory: (invoice: ReminderInvoiceRef) => void;
	onToggleReminderShare: (invoice: ReminderInvoiceRef) => void;
}

export const toReminderInvoiceRef = (
	invoice: TableInvoiceRow
): ReminderInvoiceRef | null =>
	invoice.invoice_id
		? {
				invoiceId: invoice.invoice_id,
				docNumber: invoice.invoice_doc_number ?? "",
				paused: !!invoice.reminder_paused,
				pausedUntil: invoice.reminder_paused_until ?? null,
				stopReason: invoice.reminder_stop_reason ?? null,
			}
		: null;

// Context menu actions interface
export interface InvoiceTableContextMenuActions {
	onOpenFile?: (fileId: string) => void;
	onOpenPaymentDialog?: (invoice: TableInvoiceRow) => void;
	onBookRemainderAsLoss?: (invoice: TableInvoiceRow) => void;
	onShareToggle?: (invoice: TableInvoiceRow) => void;
	onCancelInvoice?: (invoiceId: number) => void;
	onSendToZak?: (invoiceId: number) => void;
	onCancelPayment?: (allocationId: number, invoiceId: number, note: string) => void;
	onRebookPaymentAsAkonto?: (allocationId: number, invoiceId: number, note: string) => void;
	isZakEnabled?: boolean;
	paymentActionsReadOnly?: boolean;
}

// Table row type for tanstack table
export type TableRow<T> = Row<T>;

// Common interfaces for display name functions
export interface DisplayNameData {
	is_company: boolean | null;
	company_name: string | null;
	first_name: string | null;
	last_name: string | null;
}

export function getPdfTemplateType(
	invoice: Pick<InvoiceWithPaymentsView, "meta_data">
): string | null {
	const metaData = invoice.meta_data as unknown as {
		pdf_template_type?: string;
	} | null;
	return metaData?.pdf_template_type ?? null;
}

// Type guard functions
export function isPaymentRow(row: BaseTableRowData): row is TablePaymentRow {
	return row.isPaymentRow === true;
}

export function isInvoiceRow(row: BaseTableRowData): row is TableInvoiceRow {
	return !row.isPaymentRow;
}

// Since we use a unified view now, we only need one type for invoice data
export type InvoiceWithPaymentsView = InvoiceWithPaymentsViewType;

// Processing result interface (used for both CAMT54 and ZAK)
export interface ProcessedTransaction {
	invoice_id: number | null;
	invoice_doc_number: string | null;
	invoice_date: string | null;
	client_name: string | null;
	amount: number;
	currency: string;
	valuta_date?: string; // Optional - only for payment processing
	booking_date?: string; // Optional - only for payment processing
	remittance_information?: string; // Optional - only for payment processing
	success: boolean;
	error?: string;
	file_name?: string; // Optional - only for file-based processing
	already_processed?: boolean; // Optional - for CAMT54 duplicates
}
